import { getCountries } from "react-phone-number-input";
import { z } from "zod";

export type CountryOption = {
  code: IsoCountryCode;
  label: string;
};

declare const isoCountryCodeBrand: unique symbol;
export type IsoCountryCode = string & {
  readonly [isoCountryCodeBrand]: true;
};
export const DEFAULT_COUNTRY_CODE = "CA" as IsoCountryCode;

// The phone dataset also includes exceptional calling-code territories that
// are not assigned ISO 3166-1 alpha-2 codes. The server intentionally rejects
// them, so they must not appear as selectable address countries.
const NON_ISO_PHONE_COUNTRY_CODES = new Set(["AC", "TA", "XK"]);
const ISO_CODES_WITHOUT_PHONE_METADATA = [
  "AQ",
  "BV",
  "GS",
  "HM",
  "PN",
  "TF",
  "UM",
];
const COUNTRY_CODES = [
  ...getCountries().filter((code) => !NON_ISO_PHONE_COUNTRY_CODES.has(code)),
  ...ISO_CODES_WITHOUT_PHONE_METADATA,
] as IsoCountryCode[];

function displayNames(locale: string) {
  return new Intl.DisplayNames([locale], { type: "region" });
}

export function getCountryOptions(locale: string): CountryOption[] {
  const names = displayNames(locale);
  return COUNTRY_CODES.map((code) => ({
    code,
    label: names.of(code) ?? code,
  })).sort((a, b) => a.label.localeCompare(b.label, locale));
}

export function countryCodeForValue(value: string): IsoCountryCode | undefined {
  const normalized = value.trim();
  const possibleCode = normalized.toUpperCase() as IsoCountryCode;
  if (COUNTRY_CODES.includes(possibleCode)) return possibleCode;

  const folded = normalized.toLocaleLowerCase();
  for (const locale of ["en", "zh"]) {
    const names = displayNames(locale);
    const match = COUNTRY_CODES.find(
      (code) => names.of(code)?.toLocaleLowerCase() === folded
    );
    if (match) return match;
  }
  return undefined;
}

export const isoCountryCodeSchema = z.string().transform((value, context) => {
  const code = countryCodeForValue(value);
  if (code) return code;
  context.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Select a valid country",
  });
  return z.NEVER;
});
